import { useState, useMemo, useRef, useEffect } from 'react'
import { X, ChevronRight, ChevronLeft, ChevronDown, Check, Users, Trash2, Target, TrendingDown, ArrowLeft, Loader2 } from 'lucide-react'
import { GAME_TYPES, PRESS_TRIGGER_MODES, STROKE_PLAY_TRIGGER_MODES, getDefaultPressConfig } from '../constants/gameTypes'
import { useMatch, useUpdateMatch } from '../hooks/useMatches'
import { useCreateGame } from '../hooks/useGames'
import { useSideBets, useCreateSideBet } from '../hooks/useSideBets'
import { buildGameHandicapBreakdown, buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { hexToRgba, getInitials } from '../utils/colors'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'
import { useWizardPersistence } from '../hooks/useWizardPersistence'
import { onlineManager } from '@tanstack/react-query'
import logger from '../utils/logger'
import StepCourse from './wizard/StepCourse'
import StepGameType from './wizard/StepGameType'
import StepStrokes, { PlayerStrokesTable } from './wizard/StepStrokes'
import StepSideBets from './wizard/StepSideBets'
import FormatSelector from './wizard/FormatSelector'
import './Modal.css'

const STEPS = ['Course', 'Main Game', 'Teams', 'Strokes', 'Format & Wager', 'Presses', 'Side Games', 'Confirm']

export default function RoundSetupWizard({ matchId, onClose, onRoundCreated, embedded = false, initialState = null }) {
  const { data: match } = useMatch(matchId)
  const createGame = useCreateGame()
  const updateMatch = useUpdateMatch()
  const { data: matchSideBets } = useSideBets(matchId)
  const createSideBet = useCreateSideBet()
  const { saveState: saveWizardState, clearState: clearWizardState } = useWizardPersistence(matchId)

  const s = initialState || {}
  const [step, setStep] = useState(s.step ?? 0)
  const [matchNameOverride, setMatchNameOverride] = useState(s.matchNameOverride ?? null)
  const [selectedCourse, setSelectedCourse] = useState(s.selectedCourse ?? null)
  const [selectedTee, setSelectedTee] = useState(s.selectedTee ?? null)
  const [mixedTees, setMixedTees] = useState(s.mixedTees ?? null)
  const [numHoles, setNumHoles] = useState(s.numHoles ?? 18)
  const [gameTypeId, setGameTypeId] = useState(s.gameTypeId ?? '')
  const [matchFormat, setMatchFormat] = useState(s.matchFormat ?? 'nassau')
  const [nassauWeights, setNassauWeights] = useState(s.nassauWeights ?? [1, 1, 1])
  const [wager, setWager] = useState(s.wager ?? '')
  const [gameName, setGameName] = useState(s.gameName ?? '')
  const [teams, setTeams] = useState(s.teams ?? [])
  const [heroRotation, setHeroRotation] = useState(s.heroRotation ?? { front: [null, null], back: [null, null] })
  // Side bets for this round
  const [ctpEnabled, setCtpEnabled] = useState(s.ctpEnabled ?? false)
  const [ctpAmount, setCtpAmount] = useState(s.ctpAmount ?? '5')
  const [roundLowNetEnabled, setRoundLowNetEnabled] = useState(s.roundLowNetEnabled ?? false)
  const [roundLowNetAmount, setRoundLowNetAmount] = useState(s.roundLowNetAmount ?? '10')
  const [roundSkinsEnabled, setRoundSkinsEnabled] = useState(s.roundSkinsEnabled ?? false)
  const [roundSkinsAmount, setRoundSkinsAmount] = useState(s.roundSkinsAmount ?? '5')
  const [roundSkinsCarryOver, setRoundSkinsCarryOver] = useState(s.roundSkinsCarryOver ?? false)
  const [roundSkinsGrossTiebreaker, setRoundSkinsGrossTiebreaker] = useState(s.roundSkinsGrossTiebreaker ?? false)
  const [roundSkinsFullHandicap, setRoundSkinsFullHandicap] = useState(s.roundSkinsFullHandicap ?? false)
  // Stroke mode: 'whs' (default WHS Standard), 'straight_up', or 'custom'
  const [strokeMode, setStrokeMode] = useState(s.strokeMode ?? (s.straightUp ? 'straight_up' : 'whs'))
  const [offLowScope, setOffLowScope] = useState(s.offLowScope ?? 'matchup')
  const [customHandicapPercent, setCustomHandicapPercent] = useState(s.customHandicapPercent ?? null)
  const [customOffLow, setCustomOffLow] = useState(s.customOffLow ?? null)
  const straightUp = strokeMode === 'straight_up'
  // Pressing config
  const [pressMode, setPressMode] = useState(s.pressMode ?? 'none') // 'none' | 'standard' | 'custom'
  const [pressEnabled, setPressEnabled] = useState(s.pressEnabled ?? false)
  const [pressConfig, setPressConfig] = useState(s.pressConfig ?? (() => getDefaultPressConfig('')))
  const [showStartConfirm, setShowStartConfirm] = useState(false)
  // unassigned players pool
  const matchPlayers = match?.match_players || []

  const course = selectedCourse
  const tee = selectedTee
  const courseId = selectedCourse?.id || ''
  const teeName = selectedTee?.name || ''
  const gameType = gameTypeId ? GAME_TYPES.find((g) => g.id === gameTypeId) : null

  // Compute handicap breakdown when course + tee selected
  const handicapBreakdown = useMemo(() => {
    // Build mixedTees map for breakdown: only players whose tee differs from primary
    const mixedTeesMap = mixedTees ? Object.fromEntries(
      Object.entries(mixedTees).filter(([, t]) => t.name !== selectedTee?.name)
    ) : null
    const effectiveMixedTees = mixedTeesMap && Object.keys(mixedTeesMap).length > 0 ? mixedTeesMap : null
    return buildGameHandicapBreakdown({
      gameType: gameTypeId, teamStructure: teams, matchPlayers, course, tee,
      mixedTees: effectiveMixedTees,
      gameConfig: { straightUp, offLowScope, strokeMode, customHandicapPercent, customOffLow },
    }) || []
  }, [course, tee, mixedTees, matchPlayers, gameTypeId, teams, straightUp, offLowScope, strokeMode, customHandicapPercent, customOffLow])

  const getHandicapInfo = (playerId) => {
    return handicapBreakdown.find((h) => h.playerId === playerId)
  }

  // Course handicap map for Teams step (100% course handicap, no off-low)
  const courseHandicapMap = useMemo(() => {
    if (!tee) return {}
    const breakdown = buildSideBetHandicapBreakdown({ matchPlayers, course, tee, mixedTees })
    const map = {}
    if (breakdown) {
      breakdown.forEach(p => { map[p.playerId] = p.courseHandicap })
    }
    return map
  }, [matchPlayers, course, tee, mixedTees])

  // ---- Team management ----
  const assignedPlayerIds = teams.flatMap((t) => t.playerIds)
  const unassignedPlayers = matchPlayers.filter((mp) => !assignedPlayerIds.includes(mp.player_id))

  const initTeams = (numTeams) => {
    const newTeams = []
    for (let i = 0; i < numTeams; i++) {
      newTeams.push({
        name: `Team ${String.fromCharCode(65 + i)}`,
        playerIds: [],
      })
    }
    setTeams(newTeams)
  }

  const addPlayerToTeam = (teamIndex, playerId) => {
    setTeams((prev) => prev.map((t, i) =>
      i === teamIndex ? { ...t, playerIds: [...t.playerIds, playerId] } : t
    ))
  }

  const removePlayerFromTeam = (teamIndex, playerId) => {
    setTeams((prev) => prev.map((t, i) =>
      i === teamIndex ? { ...t, playerIds: t.playerIds.filter((id) => id !== playerId) } : t
    ))
  }

  const getWizardFields = (overrides = {}) => ({
    step, selectedCourse, selectedTee, mixedTees, numHoles, gameTypeId, matchFormat, nassauWeights, wager, gameName,
    matchNameOverride, teams, heroRotation, strokeMode, offLowScope, customHandicapPercent, customOffLow,
    ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount,
    roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker, roundSkinsFullHandicap,
    pressMode, pressEnabled, pressConfig,
    phase: 'round-setup',
    ...overrides,
  })

  // Auto-save wizard fields on any change (skip initial mount)
  const didMount = useRef(false)
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    saveWizardState(getWizardFields())
  }, [
    step, selectedCourse, selectedTee, mixedTees, numHoles, gameTypeId, matchFormat, nassauWeights, wager, gameName,
    matchNameOverride, teams, heroRotation, strokeMode, offLowScope, customHandicapPercent, customOffLow,
    ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount,
    roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker, roundSkinsFullHandicap,
    pressMode, pressEnabled, pressConfig,
  ])

  // ---- Navigation ----
  const canAdvance = () => {
    switch (step) {
      case 0: return !!courseId && !!teeName
      case 1: return !!gameTypeId
      case 2: return teams.length >= 2 && teams.every((t) => t.playerIds.length > 0) && unassignedPlayers.length === 0
      case 3: return true // Strokes always valid (has default)
      case 4: return !!matchFormat && wager !== '' && Number(wager) >= 0
      case 5: return true // Presses are optional
      case 6: return true // Side bets are optional
      case 7: return true
      default: return false
    }
  }

  const isIndividualGame = gameType?.teamSizes?.length === 1 && gameType?.teamSizes[0] === 1

  const handleNext = () => {
    let newTeams = teams
    if (step === 1 && teams.length === 0) {
      if (isIndividualGame) {
        // Individual games: auto-assign one player per "team"
        newTeams = matchPlayers.map((mp) => ({
          name: mp.player?.display_name || 'Player',
          playerIds: [mp.player_id],
        }))
      } else {
        // Auto-init teams based on game type and player count
        const playerCount = matchPlayers.length
        const teamCounts = Array.isArray(gameType.numTeams) ? gameType.numTeams : [gameType.numTeams]
        let numTeams = teamCounts[0]
        let found = false
        for (const size of gameType.teamSizes) {
          for (const count of teamCounts) {
            if (size * count === playerCount) {
              numTeams = count
              found = true
              break
            }
          }
          if (found) break
        }
        // Check mixed formats (e.g. 3v4 for 7 players)
        if (!found && gameType.mixedFormats) {
          for (const mf of gameType.mixedFormats) {
            if (mf.sizes.reduce((s, v) => s + v, 0) === playerCount) {
              numTeams = mf.numTeams
              break
            }
          }
        }
        newTeams = []
        for (let i = 0; i < numTeams; i++) {
          newTeams.push({ name: `Team ${String.fromCharCode(65 + i)}`, playerIds: [] })
        }
      }
      setTeams(newTeams)
    }
    const nextStep = Math.min(step + 1, STEPS.length - 1)
    setStep(nextStep)
  }

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const handleCancelRequest = () => {
    setShowCancelConfirm(true)
  }

  // Watchdog I: Cancel/discard timeout — fires if wizard is still mounted 5s after cancel
  const [cancelledAt, setCancelledAt] = useState(null)
  useEffect(() => {
    if (!cancelledAt) return
    const watchdog = setTimeout(() => {
      logger.capture(new Error('Watchdog I: Round cancel/discard took >5s — wizard still mounted'), {
        matchId,
        elapsed: Date.now() - cancelledAt,
        isOnline: navigator.onLine,
      })
    }, 5000)
    return () => clearTimeout(watchdog)
  }, [cancelledAt])

  const handleCancelConfirm = () => {
    setCancelledAt(Date.now())
    clearWizardState()
    setShowCancelConfirm(false)
    onClose()
  }

  const handleBack = () => {
    if (embedded && step === 0) {
      handleCancelRequest()
      return
    }
    const prevStep = Math.max(step - 1, 0)
    setStep(prevStep)
  }

  // Android back gesture mirrors the physical Back button in the wizard footer
  useEffect(() => {
    const handler = (e) => {
      e.detail.handled = true
      if (step === 0 && !embedded) {
        handleCancelRequest()
      } else {
        handleBack()
      }
    }
    window.addEventListener('press:back-gesture', handler)
    return () => window.removeEventListener('press:back-gesture', handler)
  }, [step, embedded])

  // Watchdog G: Round start timeout — fires if createGame doesn't resolve within 10s
  const roundStartWatchdog = useRef(null)

  const handleSubmit = () => {
    if (!onlineManager.isOnline()) {
      logger.capture(new Error('Round start blocked — offline'), {
        matchId: match?.id, gameType: gameTypeId,
      })
      alert('Unable to start round — no network connection. Please try again when connectivity is restored.')
      return
    }
    // Start watchdog timer
    clearTimeout(roundStartWatchdog.current)
    roundStartWatchdog.current = setTimeout(() => {
      logger.capture(new Error('Watchdog G: Round start timeout — createGame did not resolve within 10s'), {
        matchId,
        gameType: gameTypeId,
        isOnline: navigator.onLine,
      })
    }, 10000)
    // Save match name if user edited it
    if (matchNameOverride !== null && matchNameOverride.trim() !== (match?.match_name || '')) {
      updateMatch.mutate({ id: matchId, match_name: matchNameOverride.trim() })
    }

    const allPlayerIds = matchPlayers.map((mp) => mp.player_id)

    // Build game_config
    const gameConfig = {}
    if (matchFormat) {
      gameConfig.matchFormat = matchFormat
    }
    if (gameTypeId === 'hero') {
      gameConfig.heroRotation = heroRotation
    }
    if (matchFormat === 'nassau' && nassauWeights && nassauWeights.some((w, i) => w !== 1)) {
      gameConfig.nassauWeights = nassauWeights
    }
    if (pressEnabled) {
      gameConfig.pressConfig = pressConfig
    }
    if (strokeMode !== 'whs') gameConfig.strokeMode = strokeMode
    if (strokeMode === 'straight_up') gameConfig.straightUp = true
    if (strokeMode === 'custom') {
      gameConfig.customHandicapPercent = customHandicapPercent
      gameConfig.customOffLow = customOffLow
    }
    if (offLowScope !== 'session') gameConfig.offLowScope = offLowScope
    // Snapshot course data so scoring is self-contained
    if (course && tee) {
      gameConfig.courseData = {
        id: course.id,
        name: course.name || course.course_name,
        shortName: course.shortName || course.short_name || course.name || course.course_name,
        holes: course.holes,
        par: [...course.par],
        handicapIndex: [...(course.handicapIndex || course.handicap_index || [])],
        tee: {
          name: tee.name,
          rating: tee.rating,
          slope: tee.slope,
          ...(tee.womenRating != null && { womenRating: tee.womenRating }),
          ...(tee.womenSlope != null && { womenSlope: tee.womenSlope }),
          ...(tee.womenHandicapIndex && { womenHandicapIndex: [...tee.womenHandicapIndex] }),
          totalYardage: tee.totalYardage,
          yardages: [...(tee.yardages || [])],
        },
      }
      // Snapshot mixed tees — only players whose tee differs from primary
      if (mixedTees) {
        const diffTees = {}
        for (const [pid, t] of Object.entries(mixedTees)) {
          if (t.name !== tee.name) {
            diffTees[pid] = {
              name: t.name,
              rating: t.rating,
              slope: t.slope,
              ...(t.womenRating != null && { womenRating: t.womenRating }),
              ...(t.womenSlope != null && { womenSlope: t.womenSlope }),
              ...(t.womenHandicapIndex && { womenHandicapIndex: [...t.womenHandicapIndex] }),
              totalYardage: t.totalYardage,
              yardages: [...(t.yardages || [])],
              ...(t.handicapIndex && { handicapIndex: [...t.handicapIndex] }),
            }
          }
        }
        if (Object.keys(diffTees).length > 0) {
          gameConfig.courseData.mixedTees = diffTees
        }
      }
    }

    createGame.mutate(
      {
        match_id: matchId,
        game_name: gameName.trim() || null,
        game_type: gameTypeId,
        game_config: gameConfig,
        course_name: course?.name || null,
        course_id: courseId,
        tee_name: teeName,
        wager: parseFloat(wager) || 0,
        num_holes: numHoles,
        player_ids: allPlayerIds,
        teams: teams.map((t) => ({ name: t.name, playerIds: t.playerIds })),
      },
      {
        onSuccess: async (game) => {
          clearTimeout(roundStartWatchdog.current)
          // Create round-level side bets
          if (ctpEnabled) {
            createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'ctp', amount: parseFloat(ctpAmount) || 5, scope: 'round' })
          }
          if (roundLowNetEnabled) {
            createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'low_net', amount: parseFloat(roundLowNetAmount) || 10, scope: 'round' })
          }
          if (roundSkinsEnabled) {
            const betType = roundSkinsCarryOver ? 'skins_carry' : 'skins'
            const skinsConfig = (roundSkinsGrossTiebreaker || roundSkinsFullHandicap) ? { ...(roundSkinsGrossTiebreaker && { gross_tiebreaker: true }), ...(roundSkinsFullHandicap && { full_handicap: true }) } : undefined
            createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: betType, amount: parseFloat(roundSkinsAmount) || 5, scope: 'round', config: skinsConfig })
          }
          clearWizardState()
          onRoundCreated?.(game)
          onClose()
        },
        onError: (error) => {
          clearTimeout(roundStartWatchdog.current)
          logger.capture(new Error('Round start failed: ' + error?.message), {
            matchId,
            gameType: gameTypeId,
            isOnline: navigator.onLine,
          })
          alert('Failed to start round. Check your connection and try again.')
        },
      }
    )
  }

  const getPlayerByPlayerId = (playerId) => {
    const mp = matchPlayers.find((mp) => mp.player_id === playerId)
    return mp?.player
  }

  const stepIndicatorContainerRef = useRef(null)

  useEffect(() => {
    const el = stepIndicatorContainerRef.current
    if (!el) return
    const activeStep = el.querySelector('[data-active-step]')
    if (activeStep) {
      const scrollLeft = activeStep.offsetLeft - el.offsetWidth / 2 + activeStep.offsetWidth / 2
      el.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' })
    }
  }, [step])

  const stepIndicator = (
    <div
      ref={stepIndicatorContainerRef}
      className="hide-scrollbar"
      data-sticky
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: embedded ? '0.75rem 0' : '0.75rem 1.25rem',
        gap: '0.25rem',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '0.5rem',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'var(--bg-primary)',
        ...(embedded ? { position: 'sticky', top: '-1rem', zIndex: 9 } : {}),
      }}
    >
      {STEPS.map((label, i) => (
        <div
          key={label}
          {...(i === step ? { 'data-active-step': true } : {})}
          style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            opacity: i <= step ? 1 : 0.4,
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: i < step ? 'var(--accent-primary)' : i === step ? 'var(--accent-secondary)' : 'var(--bg-tertiary)',
              color: i <= step ? 'white' : 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6875rem',
              fontWeight: '700',
              flexShrink: 0,
            }}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <span style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              color: i === step ? 'var(--text-primary)' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              width: '24px',
              height: '1px',
              backgroundColor: 'var(--border-color)',
              margin: '0 0.25rem',
              flexShrink: 0,
            }} />
          )}
        </div>
      ))}
    </div>
  )

  const stepContent = (
    <div className={embedded ? undefined : 'modal-form'} style={{ flex: 1, overflowY: 'auto' }}>
          {step === 0 && (() => {
            const labels = getMatchTypeLabels(match?.match_type)
            const showMatchName = match?.match_type === 'quick_round' || !match?.match_type
            return (
              <StepCourse
                selectedCourse={selectedCourse}
                onSelectCourse={(c) => { setSelectedCourse(c); setSelectedTee(null); setMixedTees(null) }}
                selectedTee={selectedTee}
                onSelectTee={(t) => { setSelectedTee(t); setMixedTees(null) }}
                numHoles={numHoles}
                setNumHoles={setNumHoles}
                matchPlayers={matchPlayers}
                mixedTees={mixedTees}
                setMixedTees={setMixedTees}
                courseTees={selectedCourse?.tees}
                extraNameSection={showMatchName ? (
                  <div className="form-group">
                    <label>{labels.nameLabel}</label>
                    <input
                      type="text"
                      value={matchNameOverride ?? match?.match_name ?? ''}
                      onChange={(e) => setMatchNameOverride(e.target.value)}
                      placeholder="e.g. Scottsdale 2026"
                    />
                  </div>
                ) : null}
                nameLabel={!showMatchName ? 'Round Name (optional)' : null}
                namePlaceholder="e.g. Day 1 Morning"
                nameValue={gameName}
                onNameChange={setGameName}
              />
            )
          })()}
          {step === 1 && (
            <StepGameType
              gameTypeId={gameTypeId}
              setGameTypeId={(id) => {
                setGameTypeId(id)
                setTeams([])
                setPressConfig(getDefaultPressConfig(id))
                setStrokeMode('whs')
                setOffLowScope('matchup')
                setCustomHandicapPercent(null)
                setCustomOffLow(null)
              }}
              playerCount={matchPlayers.length}
            />
          )}
          {step === 2 && (
            <StepTeams
              teams={teams}
              setTeams={setTeams}
              unassignedPlayers={unassignedPlayers}
              matchPlayers={matchPlayers}
              addPlayerToTeam={addPlayerToTeam}
              removePlayerFromTeam={removePlayerFromTeam}
              initTeams={initTeams}
              gameType={gameType}
              courseHandicapMap={courseHandicapMap}
              getPlayerByPlayerId={getPlayerByPlayerId}
              heroRotation={heroRotation}
              setHeroRotation={setHeroRotation}
              isIndividualGame={isIndividualGame}
            />
          )}
          {step === 3 && (
            <StepStrokes
              gameTypeId={gameTypeId}
              strokeMode={strokeMode}
              setStrokeMode={setStrokeMode}
              offLowScope={offLowScope}
              setOffLowScope={setOffLowScope}
              customHandicapPercent={customHandicapPercent}
              setCustomHandicapPercent={setCustomHandicapPercent}
              customOffLow={customOffLow}
              setCustomOffLow={setCustomOffLow}
              showOffLowScope={false}
              handicapBreakdown={handicapBreakdown}
              matchPlayers={matchPlayers}
              getPlayerByPlayerId={getPlayerByPlayerId}
              teams={teams}
              isIndividualGame={isIndividualGame}
              courseHandicapMap={courseHandicapMap}
            />
          )}
          {step === 4 && (
            <StepFormatAndWager
              matchFormat={matchFormat}
              setMatchFormat={setMatchFormat}
              nassauWeights={nassauWeights}
              setNassauWeights={setNassauWeights}
              wager={wager}
              setWager={setWager}
              numHoles={numHoles}
              gameTypeId={gameTypeId}
            />
          )}
          {step === 5 && (
            <StepPresses
              gameTypeId={gameTypeId}
              pressMode={pressMode}
              setPressMode={setPressMode}
              pressEnabled={pressEnabled}
              setPressEnabled={setPressEnabled}
              pressConfig={pressConfig}
              setPressConfig={setPressConfig}
            />
          )}
          {step === 6 && (
            <StepSideBets
              gameTypeId={gameTypeId}
              course={course}
              ctpEnabled={ctpEnabled}
              setCtpEnabled={setCtpEnabled}
              ctpAmount={ctpAmount}
              setCtpAmount={setCtpAmount}
              roundLowNetEnabled={roundLowNetEnabled}
              setRoundLowNetEnabled={setRoundLowNetEnabled}
              roundLowNetAmount={roundLowNetAmount}
              setRoundLowNetAmount={setRoundLowNetAmount}
              roundSkinsEnabled={roundSkinsEnabled}
              setRoundSkinsEnabled={setRoundSkinsEnabled}
              roundSkinsAmount={roundSkinsAmount}
              setRoundSkinsAmount={setRoundSkinsAmount}
              roundSkinsCarryOver={roundSkinsCarryOver}
              setRoundSkinsCarryOver={setRoundSkinsCarryOver}
              roundSkinsGrossTiebreaker={roundSkinsGrossTiebreaker}
              setRoundSkinsGrossTiebreaker={setRoundSkinsGrossTiebreaker}
              roundSkinsFullHandicap={roundSkinsFullHandicap}
              setRoundSkinsFullHandicap={setRoundSkinsFullHandicap}
              lowNetLabel="round"
              headerNote="Optional — add side games for extra action alongside the main game."
            >
              {/* Show active match-level low net as read-only */}
              {(matchSideBets || []).filter((b) => !b.game_id && b.scope === 'match').length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>
                    Active Match-Level Games
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                    {(matchSideBets || []).filter((b) => !b.game_id && b.scope === 'match').map((bet) => (
                      <span key={bet.id} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.625rem',
                        borderRadius: '1rem',
                        backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                        border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--accent-primary)',
                      }}>
                        <TrendingDown size={12} />
                        Low Net ${Number(bet.amount)} (entire match)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </StepSideBets>
          )}
          {step === 7 && (
            <StepConfirm
              course={course}
              tee={tee}
              numHoles={numHoles}
              gameType={gameType}
              matchFormat={matchFormat}
              wager={wager}
              gameName={gameName}
              teams={teams}
              matchPlayers={matchPlayers}
              getPlayerByPlayerId={getPlayerByPlayerId}
              ctpEnabled={ctpEnabled}
              ctpAmount={ctpAmount}
              roundLowNetEnabled={roundLowNetEnabled}
              roundLowNetAmount={roundLowNetAmount}
              roundSkinsEnabled={roundSkinsEnabled}
              roundSkinsAmount={roundSkinsAmount}
              roundSkinsCarryOver={roundSkinsCarryOver}
              roundSkinsGrossTiebreaker={roundSkinsGrossTiebreaker}
              roundSkinsFullHandicap={roundSkinsFullHandicap}
              pressEnabled={pressEnabled}
              pressConfig={pressConfig}
              isIndividualGame={isIndividualGame}
              handicapBreakdown={handicapBreakdown}
              courseHandicapMap={courseHandicapMap}
            />
          )}
        </div>
  )

  const getStepHint = () => {
    switch (step) {
      case 0:
        if (!courseId) return { text: 'Select a course to continue', color: '#ef4444' }
        if (!teeName) return { text: 'Select tees to continue', color: '#ef4444' }
        return null
      case 1:
        if (!gameTypeId) return { text: 'Choose a game type to continue', color: '#ef4444' }
        return null
      case 2:
        if (unassignedPlayers.length > 0) return { text: 'Assign all players to a team to continue', color: '#ef4444' }
        return null
      case 3:
        return null
      case 4:
        if (wager === '' || Number(wager) < 0) return { text: 'Enter stakes to continue', color: '#ef4444' }
        return null
      default:
        return null
    }
  }

  const stepHint = getStepHint()

  const actions = (
    <div style={{
      position: 'fixed',
      bottom: 'var(--keyboard-height, 0px)',
      left: 0,
      right: 0,
      padding: '0.75rem 1rem',
      paddingBottom: 'var(--action-bar-pad-bottom)',
      backgroundColor: 'var(--bg-primary)',
      borderTop: '1px solid var(--border-color)',
      zIndex: 1001,
    }}>
      {stepHint && (
        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: stepHint.color, marginBottom: '0.375rem' }}>
          {stepHint.text}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {step > 0 || embedded ? (
          <button type="button" className="btn-secondary btn-action" onClick={handleBack}>
            <ChevronLeft size={18} />
            Back
          </button>
        ) : (
          <button type="button" className="btn-secondary btn-action" onClick={handleCancelRequest}>
            Cancel
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            className="btn-primary btn-action"
            onClick={handleNext}
            disabled={!canAdvance()}
            style={{ opacity: canAdvance() ? 1 : 0.5 }}
          >
            Next
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary btn-action"
            onClick={() => setShowStartConfirm(true)}
          >
            Start Round
          </button>
        )}
      </div>
    </div>
  )

  const confirmModal = showStartConfirm && (
    <div
      onClick={() => !createGame.isPending && setShowStartConfirm(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '1rem',
          border: '1px solid var(--border-color)',
          padding: '1.5rem',
          maxWidth: '340px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        }}
      >
        <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
          Start Round?
        </p>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
          This will create the round and open the scorecard. Teams and settings can't be changed after starting.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={() => handleSubmit()}
            disabled={createGame.isPending}
            style={{
              height: '44px',
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: '600',
              borderRadius: '9999px',
              color: '#fff',
              backgroundColor: 'var(--accent-primary)',
              border: 'none',
              cursor: 'pointer',
              opacity: createGame.isPending ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            {createGame.isPending && <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />}
            {createGame.isPending ? 'Starting Round...' : 'Confirm Start Round'}
          </button>
          {!createGame.isPending && (
            <button
              onClick={() => setShowStartConfirm(false)}
              style={{
                height: '44px',
                width: '100%',
                fontSize: '0.9375rem',
                borderRadius: '9999px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-secondary)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )

  const cancelConfirmModal = showCancelConfirm && (
    <div
      onClick={() => setShowCancelConfirm(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '1rem',
          border: '1px solid var(--border-color)',
          padding: '1.5rem',
          maxWidth: '340px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        }}
      >
        <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
          Cancel Round Setup?
        </p>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
          All selections will be lost and you'll need to start over.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={handleCancelConfirm}
            style={{
              height: '44px',
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: '600',
              borderRadius: '9999px',
              color: '#fff',
              backgroundColor: '#ef4444',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Yes, Cancel
          </button>
          <button
            onClick={() => setShowCancelConfirm(false)}
            style={{
              height: '44px',
              width: '100%',
              fontSize: '0.9375rem',
              borderRadius: '9999px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Keep Editing
          </button>
        </div>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div style={{ paddingBottom: '5rem' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.5rem',
        }}>
          <button
            onClick={handleBack}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '9999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--bg-tertiary)',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Round Setup</h1>
        </div>
        {stepIndicator}
        {stepContent}
        {actions}
        {confirmModal}
        {cancelConfirmModal}
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={handleCancelRequest}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: '5rem' }}>
        {/* Sticky header + step indicator group */}
        <div style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--bg-primary)' }}>
          <div className="modal-header" style={{ position: 'static' }}>
            <h2>New Round</h2>
            <button className="close-btn" onClick={handleCancelRequest}>
              <X size={24} />
            </button>
          </div>
          {stepIndicator}
        </div>
        {stepContent}
        {actions}
      </div>
      {confirmModal}
      {cancelConfirmModal}
    </div>
  )
}

// Step 4: Format & Wager
// ============================================
function StepFormatAndWager({ matchFormat, setMatchFormat, nassauWeights, setNassauWeights, wager, setWager, numHoles, gameTypeId }) {
  const gt = gameTypeId ? GAME_TYPES.find(g => g.id === gameTypeId) : null
  const isPerNine = gt?.scoring === 'per_nine'

  const isNassau = matchFormat === 'nassau'
  const wagerNum = Number(wager) || 0
  const weightSum = isNassau && nassauWeights ? nassauWeights.reduce((a, b) => a + b, 0) : 3
  const numSegments = isPerNine ? 2 : isNassau ? weightSum : 1

  return (
    <div>
      {!isPerNine && (
        <FormatSelector
          matchFormat={matchFormat}
          setMatchFormat={setMatchFormat}
          nassauWeights={nassauWeights}
          setNassauWeights={setNassauWeights}
          numHoles={numHoles}
          gameTypeId={gameTypeId}
        />
      )}

      <div className="form-group" style={{ marginTop: '1rem' }}>
        <label>{isNassau || isPerNine ? 'Stakes per Segment' : 'Stakes'}</label>
        {isPerNine && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem', marginBottom: '0.375rem' }}>
            Each round has 2 segments: front 9 and back 9
          </p>
        )}
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontWeight: '600',
            pointerEvents: 'none',
          }}>$</span>
          <input
            type="number"
            min="0"
            step="1"
            value={wager}
            onChange={(e) => setWager(e.target.value)}
            placeholder="e.g. 20"
            required
            style={{ paddingLeft: '1.75rem' }}
          />
        </div>
        {wagerNum > 0 && numSegments > 1 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
            {numSegments} × ${wagerNum} = ${wagerNum * numSegments}/pp total
          </p>
        )}
        {wagerNum > 0 && numSegments === 1 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
            ${wagerNum}/pp total
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================
// Step 6: Presses
// ============================================
function StepPresses({ gameTypeId, pressMode, setPressMode, pressEnabled, setPressEnabled, pressConfig, setPressConfig }) {
  const gt = GAME_TYPES.find(g => g.id === gameTypeId)
  const isStrokePlay = gt?.pressType === 'stroke_play'
  const [showTriggerDropdown, setShowTriggerDropdown] = useState(false)
  const triggerDropdownRef = useRef(null)
  const [showLimitDropdown, setShowLimitDropdown] = useState(false)
  const limitDropdownRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (triggerDropdownRef.current && !triggerDropdownRef.current.contains(e.target)) {
        setShowTriggerDropdown(false)
      }
      if (limitDropdownRef.current && !limitDropdownRef.current.contains(e.target)) {
        setShowLimitDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleModeChange = (mode) => {
    setPressMode(mode)
    if (mode === 'none') {
      setPressEnabled(false)
    } else if (mode === 'standard') {
      setPressEnabled(true)
      if (isStrokePlay) {
        setPressConfig({
          triggerMode: null,
          strokePlayTrigger: '1_down',
          strokePlayThreshold: null,
          lastHolePress: false,
          lastHoleHoles: [],
          pressOnPress: true,
          pressLimit: 'unlimited',
        })
      } else {
        setPressConfig({
          triggerMode: '1_down',
          strokePlayTrigger: null,
          strokePlayThreshold: null,
          lastHolePress: false,
          lastHoleHoles: [],
          pressOnPress: true,
          pressLimit: 'unlimited',
        })
      }
    } else {
      // custom — enable pressing, keep current config
      setPressEnabled(true)
    }
  }

  const modes = [
    {
      id: 'none',
      label: 'No Pressing',
      description: 'No presses during the round',
    },
    {
      id: 'standard',
      label: 'Standard',
      description: '1-down threshold, press-on-press enabled',
    },
    {
      id: 'custom',
      label: 'Custom',
      description: 'Configure press rules, limits, and options',
    },
  ]

  return (
    <div>
      <div className="form-section">
        <h3>Pressing</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.5rem', lineHeight: 1.4 }}>
          Pressing starts a new segment that runs alongside the current segments. Select the configuration on when presses are allowed and any limits. When a Press is initiated it cannot be declined by the opposing team.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {modes.map(mode => {
            const selected = pressMode === mode.id
            return (
              <div
                key={mode.id}
                onClick={() => handleModeChange(mode.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: selected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                  borderRadius: '0.5rem',
                  border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {selected && (
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'var(--accent-primary)' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{mode.label}</span>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                    {mode.description}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Custom options */}
        {pressMode === 'custom' && (() => {
          const triggerModes = isStrokePlay ? STROKE_PLAY_TRIGGER_MODES : PRESS_TRIGGER_MODES
          const currentTrigger = isStrokePlay ? pressConfig.strokePlayTrigger : pressConfig.triggerMode

          return (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Press trigger (trigger mode) */}
              <div ref={triggerDropdownRef}>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem', display: 'block' }}>
                  Press Trigger
                </label>
                <button
                  type="button"
                  onClick={() => setShowTriggerDropdown(!showTriggerDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      {triggerModes.find(m => m.id === currentTrigger)?.label || 'Select trigger'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                      {triggerModes.find(m => m.id === currentTrigger)?.description || ''}
                    </span>
                  </div>
                  <ChevronDown size={16} style={{
                    color: 'var(--text-muted)',
                    transition: 'transform 0.2s',
                    transform: showTriggerDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                  }} />
                </button>
                {showTriggerDropdown && (
                  <div style={{
                    marginTop: '0.25rem',
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}>
                    {triggerModes.map((m, idx) => {
                      const isSelected = currentTrigger === m.id
                      return (
                        <button
                          key={m.id}
                          onClick={() => {
                            const lateHoleUpdate = m.id === 'none' ? { lastHolePress: false, lastHoleHoles: [] } : {}
                            if (isStrokePlay) {
                              setPressConfig(prev => ({ ...prev, strokePlayTrigger: m.id, ...lateHoleUpdate }))
                            } else {
                              setPressConfig(prev => ({ ...prev, triggerMode: m.id, ...lateHoleUpdate }))
                            }
                            setShowTriggerDropdown(false)
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '0.625rem 0.75rem',
                            textAlign: 'left',
                            borderBottom: idx < triggerModes.length - 1 ? '1px solid var(--border-color)' : 'none',
                            backgroundColor: isSelected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'transparent',
                            borderRadius: idx === 0 ? '0.5rem 0.5rem 0 0' : idx === triggerModes.length - 1 ? '0 0 0.5rem 0.5rem' : '0',
                            cursor: 'pointer',
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{m.label}</span>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>{m.description}</div>
                          </div>
                          {isSelected && <Check size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Last Hole Press — hidden when trigger is 'none' (press any time makes this redundant) */}
              {currentTrigger !== 'none' && <div
                onClick={() => {
                  const newVal = !pressConfig.lastHolePress
                  setPressConfig(prev => ({
                    ...prev,
                    lastHolePress: newVal,
                    lastHoleHoles: newVal ? (prev.lastHoleHoles?.length ? prev.lastHoleHoles : [16, 17, 18]) : [],
                  }))
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
                  padding: '0.625rem 0.75rem', borderRadius: '0.5rem',
                  backgroundColor: pressConfig.lastHolePress ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'var(--bg-tertiary)',
                  border: `1px solid ${pressConfig.lastHolePress ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '0.1875rem',
                  border: `2px solid ${pressConfig.lastHolePress ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: pressConfig.lastHolePress ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {pressConfig.lastHolePress && <Check size={10} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>Allow Late Hole Press</span>
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Either team can press on selected late holes, regardless of standing</div>
                  {/* Per-hole late press selection */}
                  {pressConfig.lastHolePress && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {[16, 17, 18].map(hole => {
                        const selected = (pressConfig.lastHoleHoles || []).includes(hole)
                        return (
                          <div
                            key={hole}
                            onClick={(e) => {
                              e.stopPropagation()
                              const current = pressConfig.lastHoleHoles || []
                              const next = selected ? current.filter(h => h !== hole) : [...current, hole].sort()
                              // Require at least one hole selected
                              if (next.length === 0) return
                              setPressConfig(prev => ({ ...prev, lastHoleHoles: next }))
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer',
                              padding: '0.375rem 0.625rem', borderRadius: '0.375rem',
                              backgroundColor: selected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.12)' : 'var(--bg-tertiary)',
                              border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                            }}
                          >
                            <div style={{
                              width: '14px', height: '14px', borderRadius: '0.1875rem',
                              border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                              backgroundColor: selected ? 'var(--accent-primary)' : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              {selected && <Check size={8} style={{ color: '#fff' }} />}
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>Hole {hole}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>}

              {/* Press on Press */}
              <div
                onClick={() => setPressConfig(prev => ({ ...prev, pressOnPress: !prev.pressOnPress }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
                  padding: '0.625rem 0.75rem', borderRadius: '0.5rem',
                  backgroundColor: pressConfig.pressOnPress ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'var(--bg-tertiary)',
                  border: `1px solid ${pressConfig.pressOnPress ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '0.1875rem',
                  border: `2px solid ${pressConfig.pressOnPress ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: pressConfig.pressOnPress ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {pressConfig.pressOnPress && <Check size={10} style={{ color: '#fff' }} />}
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>Press on Press</span>
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Press an existing press</div>
                </div>
              </div>

              {/* Press limit */}
              <div ref={limitDropdownRef}>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem', display: 'block' }}>
                  Press Limit
                </label>
                <button
                  type="button"
                  onClick={() => setShowLimitDropdown(!showLimitDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {pressConfig.pressLimit === 'unlimited' ? 'Unlimited' : pressConfig.pressLimit}
                  </span>
                  <ChevronDown size={16} style={{
                    color: 'var(--text-muted)',
                    transition: 'transform 0.2s',
                    transform: showLimitDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                  }} />
                </button>
                {showLimitDropdown && (() => {
                  const options = [{ value: 'unlimited', label: 'Unlimited' }, ...[1,2,3,4,5,6,7,8,9].map(n => ({ value: n, label: String(n) }))]
                  return (
                    <div style={{
                      marginTop: '0.25rem',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}>
                      {options.map((opt, idx) => {
                        const isSelected = String(pressConfig.pressLimit) === String(opt.value)
                        return (
                          <button
                            key={opt.value}
                            onClick={() => {
                              setPressConfig(prev => ({ ...prev, pressLimit: opt.value === 'unlimited' ? 'unlimited' : Number(opt.value) }))
                              setShowLimitDropdown(false)
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '0.625rem 0.75rem',
                              textAlign: 'left',
                              borderBottom: idx < options.length - 1 ? '1px solid var(--border-color)' : 'none',
                              backgroundColor: isSelected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'transparent',
                              borderRadius: idx === 0 ? '0.5rem 0.5rem 0 0' : idx === options.length - 1 ? '0 0 0.5rem 0.5rem' : '0',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{opt.label}</span>
                            {isSelected && <Check size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                          </button>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// ============================================
// Step 5: Teams
// ============================================
function StepTeams({ teams, setTeams, unassignedPlayers, matchPlayers, addPlayerToTeam, removePlayerFromTeam, initTeams, gameType, courseHandicapMap, getPlayerByPlayerId, heroRotation, setHeroRotation, isIndividualGame }) {
  const playerCount = matchPlayers.length
  const allTeamCounts = gameType ? (Array.isArray(gameType.numTeams) ? gameType.numTeams : [gameType.numTeams]) : [2]
  const validTeamCounts = playerCount > 0
    ? allTeamCounts.filter(count => gameType.teamSizes.some(size => size * count === playerCount))
    : allTeamCounts
  const [addingToTeam, setAddingToTeam] = useState(null)

  const addTeam = () => {
    setTeams((prev) => [...prev, { name: `Team ${String.fromCharCode(65 + prev.length)}`, playerIds: [] }])
  }

  const removeTeam = (index) => {
    setTeams((prev) => prev.filter((_, i) => i !== index))
  }

  const renameTeam = (index, name) => {
    setTeams((prev) => prev.map((t, i) => i === index ? { ...t, name } : t))
  }

  // Simplified view for individual games (1 player per team, auto-assigned)
  if (isIndividualGame) {
    return (
      <div>
        <div style={{
          padding: '0.625rem 0.75rem',
          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
          marginBottom: '1rem',
          textAlign: 'center',
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
            Individual game — each player competes independently
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {teams.map((team) => {
            const pid = team.playerIds[0]
            const player = getPlayerByPlayerId(pid)
            if (!player) return null
            return (
              <div
                key={pid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: player.avatar_color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.75rem',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(player)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                    {player.display_name}
                  </div>
                </div>
                {courseHandicapMap[pid] != null && (
                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
                    CH: {courseHandicapMap[pid]}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Team count selector for games with variable team counts */}
      {validTeamCounts.length > 1 && (
        <div className="form-group">
          <label>Number of Teams</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {validTeamCounts.map((n) => (
              <button
                key={n}
                type="button"
                className={teams.length === n ? 'btn-primary' : 'btn-secondary'}
                onClick={() => initTeams(n)}
                style={{ flex: 1 }}
              >
                {n} Teams
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Unassigned players count banner */}
      {unassignedPlayers.length > 0 && (
        <div style={{
          padding: '0.5rem 0.75rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px dashed var(--border-color)',
          textAlign: 'center',
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
            {unassignedPlayers.length} player{unassignedPlayers.length !== 1 ? 's' : ''} unassigned
          </span>
        </div>
      )}

      {/* Team columns */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {teams.map((team, teamIdx) => (
          <div
            key={teamIdx}
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <input
                type="text"
                value={team.name}
                onChange={(e) => renameTeam(teamIdx, e.target.value)}
                style={{
                  fontWeight: '700',
                  fontSize: '0.875rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  padding: '0.25rem 0',
                  width: '120px',
                }}
              />
              {teams.length > 2 && (
                <button
                  onClick={() => removeTeam(teamIdx)}
                  style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Players on this team */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
              {team.playerIds.map((pid) => {
                const player = getPlayerByPlayerId(pid)
                if (!player) return null
                return (
                  <div
                    key={pid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0.5rem',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '0.375rem',
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: player.avatar_color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '0.625rem',
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                      {getInitials(player)}
                    </div>
                    <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                      {player.display_name}
                    </span>
                    {courseHandicapMap[pid] != null && (
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
                        CH: {courseHandicapMap[pid]}
                      </span>
                    )}
                    {addingToTeam === teamIdx && (
                      <button
                        onClick={() => removePlayerFromTeam(teamIdx, pid)}
                        style={{ color: '#ef4444', padding: '0.25rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )
              })}
              {team.playerIds.length === 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem 0' }}>
                  No players yet
                </p>
              )}
            </div>

            {/* Add/Edit Players button + expanded player list */}
            {addingToTeam !== teamIdx && (unassignedPlayers.length > 0 || team.playerIds.length > 0) && (
              <button
                onClick={() => setAddingToTeam(teamIdx)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.375rem',
                  padding: '0.625rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                  border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
                  color: 'var(--accent-primary)',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                }}
              >
                <Users size={16} />
                Add / Edit Players
              </button>
            )}

            {addingToTeam === teamIdx && (
              <div style={{
                borderRadius: '0.5rem',
                border: '1px solid var(--accent-primary)',
                overflow: 'hidden',
              }}>
                {unassignedPlayers.length > 0 && (
                  <>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                      borderBottom: '1px solid var(--border-color)',
                    }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                        Select players to add
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {unassignedPlayers.map((mp, i) => {
                        const player = mp.player
                        if (!player) return null
                        return (
                          <button
                            key={mp.player_id}
                            onClick={() => addPlayerToTeam(teamIdx, mp.player_id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.625rem',
                              padding: '0.75rem',
                              backgroundColor: 'var(--bg-tertiary)',
                              borderBottom: '1px solid var(--border-color)',
                              textAlign: 'left',
                            }}
                          >
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: player.avatar_color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              fontSize: '0.75rem',
                              color: '#fff',
                              flexShrink: 0,
                            }}>
                              {getInitials(player)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                {player.display_name}
                              </div>
                              {courseHandicapMap[mp.player_id] != null && (
                                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                                  CH: {courseHandicapMap[mp.player_id]}
                                </div>
                              )}
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                              Add
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </>
                )}
                <button
                  onClick={() => setAddingToTeam(null)}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                    color: 'var(--accent-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    borderTop: unassignedPlayers.length === 0 ? 'none' : undefined,
                  }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add team button for variable team counts */}
      {validTeamCounts.some((n) => n > teams.length) && (
        <button
          type="button"
          onClick={addTeam}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: '1px dashed var(--border-color)',
            color: 'var(--text-muted)',
            fontSize: '0.8125rem',
            fontWeight: '500',
          }}
        >
          + Add Team
        </button>
      )}

      {/* Hero rotation picker */}
      {gameType?.id === 'hero' && teams.length === 2 && teams.every((t) => t.playerIds.length >= 2) && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          border: '1px solid var(--border-color)',
        }}>
          <div style={{
            fontSize: '0.6875rem',
            fontWeight: '700',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            Solo Rotation (Front 9)
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Pick who plays solo on the front 9 for each team. The back 9 solo rotates automatically.
          </p>
          {teams.map((team, teamIdx) => (
            <div key={teamIdx} style={{ marginBottom: teamIdx < teams.length - 1 ? '0.5rem' : 0 }}>
              <div style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                {team.name} - Front 9 Solo:
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {team.playerIds.map((pid) => {
                  const player = getPlayerByPlayerId(pid)
                  if (!player) return null
                  const isSelected = heroRotation.front?.[teamIdx] === pid
                  return (
                    <button
                      key={pid}
                      type="button"
                      onClick={() => {
                        const newFront = [...(heroRotation.front || [null, null])]
                        newFront[teamIdx] = pid
                        // Auto-assign back 9 solo: pick second player (not front solo)
                        const backSolo = team.playerIds.find((id) => id !== pid) || team.playerIds[1]
                        const newBack = [...(heroRotation.back || [null, null])]
                        newBack[teamIdx] = backSolo
                        setHeroRotation({ front: newFront, back: newBack })
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.375rem 0.625rem',
                        borderRadius: '9999px',
                        backgroundColor: isSelected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)' : 'var(--bg-secondary)',
                        border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        fontSize: '0.75rem',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                      }}
                    >
                      {isSelected && <Check size={12} />}
                      {player.display_name}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {heroRotation.front?.some(Boolean) && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              Back 9 solo: {teams.map((team, i) => {
                const backPlayer = getPlayerByPlayerId(heroRotation.back?.[i])
                return backPlayer ? `${team.name}: ${backPlayer.display_name}` : null
              }).filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// Step 8: Confirm
// ============================================
function StepConfirm({ course, tee, numHoles, gameType, matchFormat, wager, gameName, teams, matchPlayers, getPlayerByPlayerId, ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount, roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker, roundSkinsFullHandicap, pressEnabled, pressConfig, isIndividualGame, handicapBreakdown, courseHandicapMap }) {
  const totalPar = course ? course.par.reduce((s, p) => s + p, 0) : 0
  const par3Count = course ? course.par.filter((p) => p === 3).length : 0

  return (
    <div>
      <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem' }}>Review & Start</h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Course info */}
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
        }}>
          <div style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            Course
          </div>
          <div style={{ fontWeight: '600', fontSize: '0.9375rem' }}>
            {course?.shortName}
            <span style={{ color: 'var(--text-muted)', fontWeight: '400', marginLeft: '0.5rem' }}>
              {tee?.name} tees
            </span>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
            {tee?.totalYardage} yds &middot; Par {totalPar} &middot; {tee?.rating}/{tee?.slope} &middot; {numHoles} holes
          </div>
        </div>

        {/* Game type info */}
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
        }}>
          <div style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            Game
          </div>
          <div style={{ fontWeight: '600', fontSize: '0.9375rem' }}>
            {gameType?.name}
            {gameName && (
              <span style={{ color: 'var(--text-muted)', fontWeight: '400', marginLeft: '0.5rem' }}>
                ({gameName})
              </span>
            )}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
            {matchFormat === 'standard' ? 'Standard (1 bet)' : gameType?.scoring === 'per_nine' ? 'Per Nine (2 bets)' : 'Nassau (3 bets)'}
          </div>
          {wager && Number(wager) > 0 && (
            <div style={{ color: 'var(--accent-primary)', fontSize: '0.8125rem', fontWeight: '600', marginTop: '0.125rem' }}>
              ${wager}/game per player
            </div>
          )}
        </div>

        {/* Side Games */}
        {(ctpEnabled || roundLowNetEnabled || roundSkinsEnabled) && (
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '0.5rem',
          }}>
            <div style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>
              Side Games
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {ctpEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Target size={14} style={{ color: 'var(--accent-primary)' }} />
                    <span style={{ fontSize: '0.8125rem', fontWeight: '500' }}>Closest to Pin</span>
                  </div>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--accent-primary)', fontWeight: '600' }}>
                    {par3Count} par 3s × ${Number(ctpAmount)} = ${par3Count * Number(ctpAmount)}/player
                  </span>
                </div>
              )}
              {roundLowNetEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <TrendingDown size={14} style={{ color: 'var(--accent-primary)' }} />
                    <span style={{ fontSize: '0.8125rem', fontWeight: '500' }}>Lowest Net</span>
                  </div>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--accent-primary)', fontWeight: '600' }}>
                    ${Number(roundLowNetAmount)}/player
                  </span>
                </div>
              )}
              {roundSkinsEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '14px' }}>$</span>
                    <span style={{ fontSize: '0.8125rem', fontWeight: '500' }}>
                      Skins{roundSkinsCarryOver ? ' (carry-over)' : ''}{roundSkinsGrossTiebreaker ? ' (gross TB)' : ''}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--accent-primary)', fontWeight: '600' }}>
                    ${Number(roundSkinsAmount)}/player
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pressing */}
        {pressEnabled && pressConfig && (
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '0.5rem',
          }}>
            <div style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>
              Pressing
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>&#8226;</span>
                <span style={{ fontWeight: '500' }}>
                  {(() => {
                    const trigger = pressConfig.triggerMode || pressConfig.strokePlayTrigger
                    const legacyMap = {
                      manual_1_down: '1_down', auto_1_down: '1_down',
                      manual_2_down: '2_down', auto_2_down: '2_down',
                      manual_points: '1_down', auto_points: '1_down',
                    }
                    const normalized = legacyMap[trigger] || trigger
                    const all = [...(PRESS_TRIGGER_MODES || []), ...(STROKE_PLAY_TRIGGER_MODES || [])]
                    const match = all.find(m => m.id === normalized)
                    return match ? `${match.label} — ${match.description}` : trigger
                  })()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>&#8226;</span>
                <span style={{ fontWeight: '500' }}>
                  Late hole press {pressConfig.lastHolePress ? `enabled (holes ${(pressConfig.lastHoleHoles || [16]).join(', ')})` : 'disabled'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>&#8226;</span>
                <span style={{ fontWeight: '500' }}>
                  Press on press {pressConfig.pressOnPress ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>&#8226;</span>
                <span style={{ fontWeight: '500' }}>
                  {pressConfig.pressLimit === 'unlimited' ? 'Unlimited presses' : `Limit: ${pressConfig.pressLimit} presses`}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Teams / Players */}
        <div>
          <div style={{ fontSize: '0.625rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            {isIndividualGame ? 'Players' : 'Teams'}
          </div>
          <PlayerStrokesTable
            teams={teams}
            handicapBreakdown={handicapBreakdown}
            courseHandicapMap={courseHandicapMap}
            getPlayerByPlayerId={getPlayerByPlayerId}
            gameTypeId={gameType?.id}
            isIndividualGame={isIndividualGame}
          />
        </div>
      </div>
    </div>
  )
}

// ============================================
// Shared components
// ============================================
function PlayerChip({ player, strokes }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '1rem',
      backgroundColor: hexToRgba(player.avatar_color, 0.15),
      border: `1px solid ${hexToRgba(player.avatar_color, 0.3)}`,
    }}>
      <div style={{
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: player.avatar_color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '0.5625rem',
        color: '#fff',
      }}>
        {getInitials(player)}
      </div>
      <span style={{ fontSize: '0.6875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
        {player.display_name}
      </span>
      {strokes != null && (
        <span style={{ fontSize: '0.5625rem', color: 'var(--text-muted)' }}>
          +{strokes}
        </span>
      )}
    </div>
  )
}
